import { API_URL } from "../../config";
import type { EmailThread, EmailMsg, EmailThreadStatus, EmailThreadNote, AIAnalysis, AIExtractedData, EmailStats, EmailTag, EmailHubTemplate, ThreadsResponse } from "./emailHubApi";

/* ═══════════════════════════════════════════════════════════════════════════
   EMAIL HUB v3 — API SERVICE & TYPES
   Sales Conversion · Team Collaboration · Automation · Dashboard
   ═══════════════════════════════════════════════════════════════════════════ */

const hdrs = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { ...opts, headers: { ...hdrs(), ...(opts.headers as Record<string, string> || {}) } });
  const json = await res.json();
  if (!res.ok && res.status !== 200) throw { status: res.status, ...json };
  return json.data;
}

const qs = (f: Record<string, any>): string => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
  const s = p.toString(); return s ? `?${s}` : "";
};

/* ── v3 Types ──────────────────────────────────────────────────────────── */

/**
 * Extends the v2 AIExtractedData with additional fields for sales conversion.
 * All new fields are optional so v2 payloads remain valid v3 payloads.
 */
export interface AIExtractedDataV3 extends AIExtractedData {
  budget?: string | null;
  preferred_hall?: string | null;
  food_required?: boolean;
  bar_required?: boolean;
  decor_required?: boolean;
  event_duration_hours?: number | null;
  special_requests?: string[];
  phone?: string | null;
  organization?: string | null;
}

export interface AIAnalysisV3 extends AIAnalysis {
  budget?: string | null;
  preferred_hall?: string | null;
  food_required?: boolean;
  bar_required?: boolean;
  decor_required?: boolean;
  event_duration_hours?: number | null;
  special_requests?: string[];
  phone?: string | null;
  organization?: string | null;
  next_best_action?: string | null;
  next_best_action_reason?: string | null;
}

/**
 * v3 thread extends v2, replacing ai_extracted_data with the expanded schema.
 * ai_extracted_data is REQUIRED (never undefined) — it is either a populated
 * object or null.  Every other v3 field is nullable to remain wire-compatible
 * with threads that haven't been upgraded yet.
 */
/**
 * The five-state strict AI lead-match classification. Drives which
 * conversion actions (Create Lead · Link · Ignore) the inbox shows
 * for each thread. See `LeadClassificationService::classifyLeadMatch`
 * on the backend for the exact rule set.
 */
export type LeadMatchState =
  | "full_match"        // 🟢 enough signals — show full conversion actions
  | "partial_inquiry"   // 🟡 vague — hide auto-suggest; manual override
  | "existing_match"    // 🔵 sender matches an existing lead's email
  | "non_lead"          // ⚪ vendor / invoice / greeting / spam
  | "needs_review";     // 🔴 ambiguous — human eyes needed

export interface EmailThreadV3 extends Omit<EmailThread, 'ai_extracted_data'> {
  /* ── replaces v2 field with expanded type ──────────────────────── */
  ai_extracted_data: AIExtractedDataV3 | null;

  /* ── business-object links ─────────────────────────────────────── */
  lead_id: number | null;
  booking_id: number | null;
  quote_id: number | null;

  /* ── team assignment ───────────────────────────────────────────── */
  assigned_to: number | null;
  assigned_at: string | null;
  priority: "low" | "medium" | "high" | "urgent";
  due_at: string | null;

  /* ── team state & AI next action ───────────────────────────────── */
  team_state: string | null;
  next_action: string | null;
  next_action_reason: string | null;

  /* ── lead system linking ────────────────────────────────────────── */
  linked_thread_id: number | null;
  /** Eager-loaded summary of the LeadThread this email is linked to.
   *  Populated when the backend auto-creates a lead on `full_match`,
   *  or when a rep manually links/creates one. Drives the
   *  "Lead Created Automatically — Lead #..." banner without an
   *  extra round-trip to fetch the lead. */
  linked_thread?: {
    id: number;
    lead_number: string | null;
    name: string;
    status: string;
    assigned_to: number | null;
  } | null;
  is_private: boolean;

  /* ── flags ─────────────────────────────────────────────────────── */
  follow_up_required: boolean;
  is_vip: boolean;
  last_customer_reply_at: string | null;
  last_staff_reply_at: string | null;

  /* ── Strict AI lead-match classification ───────────────────────────
     Drives the inbox's conversion-action gating. State governs which
     buttons (Create Lead · Link · Ignore) the UI shows; confidence +
     reasons surface in tooltips so the rep can verify the call. Each
     field is optional so v2 thread payloads (pre-classification
     deployment) still type-check on the V3 interface. */
  lead_match_state?: LeadMatchState | null;
  lead_match_confidence?: number | null;
  lead_match_reasons?: string[] | null;
  lead_match_classified_at?: string | null;
  lead_match_ignored_at?: string | null;

  /* ── eager-loaded relations (present when detail endpoint used) ── */
  assignee?: TeamUser | null;
  lead?: EmailHubLead | null;
  quote?: EmailHubQuote | null;
  booking?: BanquetBookingSummary | null;
  tasks?: EmailHubTask[];
}

/** Minimal booking shape returned when eager-loaded on a thread. */
export interface BanquetBookingSummary {
  id: number;
  bookingRef: string;
  agentName: string | null;
  eventType: string | null;
  eventDate: string | null;
  timeSlot: string | null;
  timeSlotLabel: string | null;
  startTime: string | null;
  endTime: string | null;
  expectedGuests: number | null;
  grandTotal: number | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  primaryCustomer?: { firstName: string; lastName: string; email1: string | null } | null;
  creator?: TeamUser | null;
  halls?: { id: number; hallName: string }[] | null;
}

export interface EmailHubLead {
  id: number;
  thread_id?: number | null;
  customer_id?: number | null;
  customer_name: string;
  email?: string | null;
  phone?: string | null;
  organization?: string | null;
  event_type?: string | null;
  event_date?: string | null;
  guest_count?: number | null;
  budget?: number | null;
  preferred_hall?: string | null;
  notes?: string | null;
  source: string;
  status: "new" | "contacted" | "qualified" | "proposal_sent" | "negotiation" | "won" | "lost";
  priority: "low" | "medium" | "high" | "urgent";
  assigned_to?: number | null;
  created_by?: number | null;
  converted_at?: string | null;
  created_at?: string;
  assignee?: TeamUser | null;
  creator?: TeamUser | null;
  thread?: EmailThread | null;
}

export interface EmailHubQuote {
  id: number;
  quote_ref: string;
  thread_id?: number | null;
  lead_id?: number | null;
  customer_id?: number | null;
  customer_name: string;
  email?: string | null;
  phone?: string | null;
  event_type?: string | null;
  event_date?: string | null;
  guest_count?: number | null;
  preferred_hall?: string | null;
  food_required: boolean;
  bar_required: boolean;
  decor_required: boolean;
  event_duration_hours?: number | null;
  line_items?: QuoteLineItem[] | null;
  subtotal: number;
  tax_amount: number;
  grand_total: number;
  notes?: string | null;
  terms?: string | null;
  status: "draft" | "sent" | "viewed" | "accepted" | "rejected" | "expired";
  valid_until?: string | null;
  created_by?: number | null;
  created_at?: string;
  creator?: TeamUser | null;
  thread?: EmailThread | null;
  lead?: EmailHubLead | null;
}

export interface QuoteLineItem {
  description: string;
  qty: number;
  unit_price: number;
  total: number;
}

export interface EmailHubTask {
  id: number;
  thread_id?: number | null;
  assigned_to?: number | null;
  title: string;
  description?: string | null;
  due_at?: string | null;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  created_by?: number | null;
  completed_at?: string | null;
  created_at?: string;
  assignee?: TeamUser | null;
  creator?: TeamUser | null;
  thread?: EmailThread | null;
}

export interface EmailHubActivity {
  id: number;
  thread_id: number;
  user_id?: number | null;
  type: string;
  description?: string | null;
  meta?: Record<string, any> | null;
  created_at: string;
  user?: TeamUser | null;
}

export interface EmailHubNotification {
  id: number;
  user_id: number;
  thread_id?: number | null;
  type: string;
  title: string;
  message?: string | null;
  is_read: boolean;
  triggered_by?: number | null;
  created_at: string;
  thread?: EmailThread | null;
}

export interface TeamUser {
  id: number;
  username: string;
  user_id?: string;
}

export interface CustomerInsights {
  total_inquiries: number;
  total_bookings: number;
  last_event_date: string | null;
  lifetime_value: number;
  common_event_type: string | null;
  last_contact_date: string | null;
  customer_ids: number[];
}

export interface DashboardStats {
  new_inquiries_today: number;
  unassigned_threads: number;
  overdue_threads: number;
  avg_first_response_minutes: number;
  converted_to_quote: number;
  converted_to_booking: number;
  closed_won: number;
  closed_lost: number;
  follow_up_required: number;
  vip_threads: number;
  open_tasks: number;
  overdue_tasks: number;
}

export interface DashboardCharts {
  inquiries_by_day: { date: string; count: number }[];
  conversion_by_stage: { name: string; color: string; count: number }[];
  response_time_trend: { date: string; avg_minutes: number }[];
  source_by_mailbox: { mailbox: string; count: number }[];
  event_type_distribution: { type: string; count: number }[];
}

export interface DashboardData {
  stats: DashboardStats;
  charts: DashboardCharts;
}

export interface EmailHubAuditLog {
  id: number;
  user_id?: number | null;
  thread_id?: number | null;
  action: string;
  entity_type?: string | null;
  entity_id?: number | null;
  old_values?: Record<string, any> | null;
  new_values?: Record<string, any> | null;
  ip_address?: string | null;
  created_at: string;
  user?: TeamUser | null;
}

export interface AutomationRule {
  id: number;
  name: string;
  trigger: string;
  action: string;
  conditions?: Record<string, any> | null;
  action_params?: Record<string, any> | null;
  is_active: boolean;
  sort_order: number;
}

export interface LeadPrefill {
  customer_name: string;
  email: string;
  phone: string;
  organization: string;
  notes: string;
  /** Venue vertical only — populated when config('leads.vertical') == 'venue'. */
  event_type?: string;
  event_date?: string;
  guest_count?: number | null;
  budget?: string;
  preferred_hall?: string;
  /** Service verticals (autism / tutoring) — populated when the vertical
   *  does NOT support venue fields. Values pass straight into the
   *  CreateLeadModal's vertical field grid and land in
   *  lead_inquiries.custom_fields on save. */
  program?: string;
  location?: string;
  child_name?: string;
  child_age?: string;
  grade?: string;
  preferred_days?: string;
  preferred_start?: string;
}

export interface QuotePrefill extends LeadPrefill {
  food_required: boolean;
  bar_required: boolean;
  decor_required: boolean;
  event_duration_hours: number | null;
  special_requests: string[];
}

export interface BookingPrefill {
  customer_name: string;
  email: string;
  phone: string;
  organization: string;
  eventType: string;
  eventDate: string;
  expectedGuests: number | null;
  publicNotes: string;
}

export interface ThreadV3Filters {
  account_id?: number;
  provider?: string;
  direction?: string;
  unread?: string;
  overdue?: string;
  tag?: string;
  intent?: string;
  status_id?: number;
  search?: string;
  customer_id?: number;
  assigned_to?: number;
  unassigned?: string;
  priority?: string;
  team_state?: string;
  follow_up?: string;
  vip?: string;
  has_lead?: string;
  has_quote?: string;
  has_booking?: string;
  linked?: string;
  not_linked?: string;
  is_private?: string;
  page?: number;
  per_page?: number;
}

export interface ThreadDetailV3Response {
  thread: EmailThreadV3;
  activities: EmailHubActivity[];
  insights: CustomerInsights;
}

/** Paginated response from the v3 threads endpoint. */
export interface ThreadsV3Response {
  data: EmailThreadV3[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/* ── Safety helpers ────────────────────────────────────────────────── */

/**
 * Safely access ai_extracted_data — never returns undefined.
 * Use this everywhere you read AI fields so the UI never crashes on null.
 */
export const getAIData = (thread: EmailThreadV3): AIExtractedDataV3 => {
  return thread.ai_extracted_data ?? ({} as AIExtractedDataV3);
};

/**
 * Normalize a raw API response into a well-typed EmailThreadV3.
 * Guarantees every required v3 field has its correct zero-value rather than
 * being undefined (which can happen if the backend omits a column).
 *
 * Accepts `Partial<EmailThreadV3>` so the spread is structurally compatible
 * without an unsafe double-cast.
 */
export const normalizeThread = (raw: Partial<EmailThreadV3>): EmailThreadV3 => ({
  // Spread the known v2 + v3 fields that came back from the API
  ...raw,

  // ── v2 required fields (provide safe defaults) ────────────────────
  id:            raw.id ?? 0,
  thread_id:     raw.thread_id ?? "",
  subject:       raw.subject ?? "",
  customer_id:   raw.customer_id ?? null,
  customer_email: raw.customer_email ?? null,
  account_id:    raw.account_id ?? null,
  provider:      raw.provider ?? "microsoft",
  message_count: raw.message_count ?? 0,
  unread_count:  raw.unread_count ?? 0,
  last_message_at: raw.last_message_at ?? null,
  first_response_at: raw.first_response_at ?? null,
  last_response_at: raw.last_response_at ?? null,
  response_delay_minutes: raw.response_delay_minutes ?? null,
  is_overdue:    raw.is_overdue ?? false,
  sla_hours:     raw.sla_hours ?? 24,
  tags:          raw.tags ?? null,
  ai_intent:     raw.ai_intent ?? null,
  ai_summary:    raw.ai_summary ?? null,
  status_id:     raw.status_id ?? null,
  is_archived:   raw.is_archived ?? false,

  // ── lead system linking ────────────────────────────────────────────
  linked_thread_id:       raw.linked_thread_id ?? null,
  linked_thread:          raw.linked_thread ?? null,
  is_private:             raw.is_private ?? false,

  // ── v3 required fields ────────────────────────────────────────────
  ai_extracted_data:      raw.ai_extracted_data ?? null,
  lead_id:                raw.lead_id ?? null,
  booking_id:             raw.booking_id ?? null,
  quote_id:               raw.quote_id ?? null,
  assigned_to:            raw.assigned_to ?? null,
  assigned_at:            raw.assigned_at ?? null,
  priority:               raw.priority ?? "medium",
  due_at:                 raw.due_at ?? null,
  team_state:             raw.team_state ?? null,
  next_action:            raw.next_action ?? null,
  next_action_reason:     raw.next_action_reason ?? null,
  follow_up_required:     raw.follow_up_required ?? false,
  is_vip:                 raw.is_vip ?? false,
  last_customer_reply_at: raw.last_customer_reply_at ?? null,
  last_staff_reply_at:    raw.last_staff_reply_at ?? null,
});

/**
 * Normalize a paginated threads response — each thread in the array
 * passes through normalizeThread so downstream code never hits undefined.
 */
export const normalizeThreadsResponse = (raw: RawPaginatedResponse): ThreadsV3Response => {
  const data = Array.isArray(raw.data)
    ? (raw.data as Partial<EmailThreadV3>[]).map(normalizeThread)
    : [];
  return {
    data,
    current_page: raw.current_page ?? 1,
    last_page:    raw.last_page ?? 1,
    per_page:     raw.per_page ?? 100,
    total:        raw.total ?? 0,
  };
};

/** Shape of the paginated JSON the Laravel backend returns. */
interface RawPaginatedResponse {
  data?: unknown[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
}

/* ── TEAM STATES (constant) ────────────────────────────────────────── */
export const TEAM_STATES = [
  "Waiting on Customer",
  "Follow Up Needed",
  "Ready for Quote",
  "Ready for Booking",
  "Closed Won",
  "Closed Lost",
] as const;

export const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"] as const;

export const NEXT_ACTIONS: Record<string, { label: string; color: string }> = {
  reply_now: { label: "Reply Now", color: "#2563eb" },
  send_follow_up: { label: "Send Follow-up", color: "#7c3aed" },
  create_quote: { label: "Create Quote", color: "#059669" },
  convert_to_booking: { label: "Convert to Booking", color: "#d97706" },
  call_customer: { label: "Call Customer", color: "#dc2626" },
  no_action: { label: "No Action Needed", color: "#6b7280" },
};

export const PRIORITY_COLORS: Record<string, string> = {
  low: "#6b7280",
  medium: "#3b82f6",
  high: "#f59e0b",
  urgent: "#ef4444",
};

export const LEAD_STATUS_COLORS: Record<string, string> = {
  new: "#3b82f6",
  contacted: "#8b5cf6",
  qualified: "#14b8a6",
  proposal_sent: "#f59e0b",
  negotiation: "#f97316",
  won: "#22c55e",
  lost: "#ef4444",
};

export const QUOTE_STATUS_COLORS: Record<string, string> = {
  draft: "#6b7280",
  sent: "#3b82f6",
  viewed: "#8b5cf6",
  accepted: "#22c55e",
  rejected: "#ef4444",
  expired: "#9ca3af",
};

/* ── v3 API Calls ──────────────────────────────────────────────────── */

// Enhanced threads — responses pass through normalizers so every
// consumer gets fully-typed data with no undefined landmines.
export const fetchThreadsV3 = async (f: ThreadV3Filters = {}): Promise<ThreadsV3Response> => {
  const raw = await apiFetch<RawPaginatedResponse>(`/email-hub/v3/threads${qs(f)}`);
  return normalizeThreadsResponse(raw);
};

/** Shape of the raw detail response before normalization. */
interface RawThreadDetailResponse {
  thread?: Partial<EmailThreadV3>;
  activities?: EmailHubActivity[];
  insights?: Partial<CustomerInsights>;
}

const EMPTY_INSIGHTS: CustomerInsights = {
  total_inquiries: 0, total_bookings: 0, last_event_date: null,
  lifetime_value: 0, common_event_type: null, last_contact_date: null,
  customer_ids: [],
};

export const fetchThreadDetailV3 = async (id: number): Promise<ThreadDetailV3Response> => {
  const raw = await apiFetch<RawThreadDetailResponse>(`/email-hub/v3/threads/${id}`);
  return {
    thread:     normalizeThread(raw.thread ?? {}),
    activities: raw.activities ?? [],
    insights:   { ...EMPTY_INSIGHTS, ...raw.insights },
  };
};

// Leads
export const fetchLeadPrefill = (threadId: number): Promise<LeadPrefill> =>
  apiFetch(`/email-hub/v3/leads/prefill/${threadId}`);
export const createLead = (data: Record<string, any>): Promise<EmailHubLead> =>
  apiFetch("/email-hub/v3/leads", { method: "POST", body: JSON.stringify(data) });
export const fetchLeads = (f: Record<string, any> = {}): Promise<any> =>
  apiFetch(`/email-hub/v3/leads${qs(f)}`);
export const updateLead = (id: number, data: Record<string, any>): Promise<EmailHubLead> =>
  apiFetch(`/email-hub/v3/leads/${id}`, { method: "PUT", body: JSON.stringify(data) });

// Quotes
export const fetchQuotePrefill = (threadId: number): Promise<QuotePrefill> =>
  apiFetch(`/email-hub/v3/quotes/prefill/${threadId}`);
export const createQuote = (data: Record<string, any>): Promise<EmailHubQuote> =>
  apiFetch("/email-hub/v3/quotes", { method: "POST", body: JSON.stringify(data) });
export const fetchQuotes = (f: Record<string, any> = {}): Promise<any> =>
  apiFetch(`/email-hub/v3/quotes${qs(f)}`);
export const updateQuote = (id: number, data: Record<string, any>): Promise<EmailHubQuote> =>
  apiFetch(`/email-hub/v3/quotes/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const fetchQuoteDetail = (id: number): Promise<EmailHubQuote> =>
  apiFetch(`/email-hub/v3/quotes/${id}`);

// Booking conversion
export const fetchBookingPrefill = (threadId: number): Promise<BookingPrefill> =>
  apiFetch(`/email-hub/v3/booking/prefill/${threadId}`);
export const convertToBooking = (data: Record<string, any>): Promise<any> =>
  apiFetch("/email-hub/v3/booking/convert", { method: "POST", body: JSON.stringify(data) });

// Assignment & priority
export const assignThread = (threadId: number, data: { assigned_to?: number | null; priority?: string; due_at?: string }): Promise<EmailThreadV3> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/assign`, { method: "PATCH", body: JSON.stringify(data) });
export const updateThreadPriority = (threadId: number, priority: string): Promise<EmailThreadV3> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/priority`, { method: "PATCH", body: JSON.stringify({ priority }) });
export const updateTeamState = (threadId: number, team_state: string): Promise<EmailThreadV3> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/team-state`, { method: "PATCH", body: JSON.stringify({ team_state }) });

// AI — v3 analyze returns the expanded AIAnalysisV3 payload
export const aiAnalyzeV3 = (threadId: number): Promise<AIAnalysisV3> =>
  apiFetch("/email-hub/ai/analyze", { method: "POST", body: JSON.stringify({ thread_id: threadId }) });

/* ── Strict AI lead-match classification ─────────────────────────────
   reclassifyLeadMatch — re-run the rule-based classifier on demand,
                         e.g. after a rep edits the email body or wants
                         to refresh the verdict.
   ignoreLeadMatch    — mark the thread as "not a lead" so the inbox
                         hides conversion actions forever.
   Backend lives at LeadController::reclassifyLeadMatch / ignoreLeadMatch.
*/
export const reclassifyLeadMatch = (threadId: number): Promise<{
  thread_id: number;
  lead_match_state: LeadMatchState | null;
  lead_match_confidence: number | null;
  lead_match_reasons: string[];
  /** Populated when reclassification flipped the thread to full_match
   *  and the backend auto-created a lead in the same request. Lets the
   *  inbox switch straight to the green "Lead Created" banner without a
   *  refetch. */
  linked_thread_id?: number | null;
}> =>
  apiFetch(`/email-hub/threads/${threadId}/reclassify-lead-match`, { method: "POST" });

export const ignoreLeadMatch = (threadId: number): Promise<{ thread_id: number }> =>
  apiFetch(`/email-hub/threads/${threadId}/ignore-lead-match`, { method: "POST" });

// Notes with mentions
export const addNoteV3 = (threadId: number, note: string): Promise<EmailThreadNote> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/notes`, { method: "POST", body: JSON.stringify({ note }) });

// Tasks
export const createTask = (data: Record<string, any>): Promise<EmailHubTask> =>
  apiFetch("/email-hub/v3/tasks", { method: "POST", body: JSON.stringify(data) });
export const updateTask = (id: number, data: Record<string, any>): Promise<EmailHubTask> =>
  apiFetch(`/email-hub/v3/tasks/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteTask = (id: number): Promise<void> =>
  apiFetch(`/email-hub/v3/tasks/${id}`, { method: "DELETE" });
export const fetchTasks = (f: Record<string, any> = {}): Promise<any> =>
  apiFetch(`/email-hub/v3/tasks${qs(f)}`);
export const fetchThreadTasks = (threadId: number): Promise<EmailHubTask[]> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/tasks`);

// Timeline
export const fetchTimeline = (threadId: number): Promise<EmailHubActivity[]> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/timeline`);

// Customer intelligence
export const fetchCustomerInsights = (threadId: number): Promise<CustomerInsights> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/insights`);
export const matchCustomer = (threadId: number): Promise<any> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/match-customer`);
export const linkCustomer = (threadId: number, customer_id: number): Promise<EmailThreadV3> =>
  apiFetch(`/email-hub/v3/threads/${threadId}/link-customer`, { method: "POST", body: JSON.stringify({ customer_id }) });

// Notifications
export const fetchNotifications = (f: Record<string, any> = {}): Promise<any> =>
  apiFetch(`/email-hub/v3/notifications${qs(f)}`);
export const fetchUnreadNotifCount = (): Promise<{ count: number }> =>
  apiFetch("/email-hub/v3/notifications/unread-count");
export const markNotifRead = (id: number): Promise<void> =>
  apiFetch(`/email-hub/v3/notifications/${id}/read`, { method: "PATCH" });
export const markAllNotifsRead = (): Promise<void> =>
  apiFetch("/email-hub/v3/notifications/read-all", { method: "PATCH" });

// Automation
export const runAutomations = (): Promise<any> =>
  apiFetch("/email-hub/v3/automations/run", { method: "POST" });
export const fetchAutomationRules = (): Promise<AutomationRule[]> =>
  apiFetch("/email-hub/v3/automations/rules");
export const updateAutomationRule = (id: number, data: Record<string, any>): Promise<AutomationRule> =>
  apiFetch(`/email-hub/v3/automations/rules/${id}`, { method: "PUT", body: JSON.stringify(data) });

// Dashboard
export const fetchDashboard = (): Promise<DashboardData> =>
  apiFetch("/email-hub/v3/dashboard");
export const fetchDashboardStats = (): Promise<DashboardStats> =>
  apiFetch("/email-hub/v3/dashboard/stats");
export const fetchDashboardCharts = (): Promise<DashboardCharts> =>
  apiFetch("/email-hub/v3/dashboard/charts");

// Audit log
export const fetchAuditLog = (f: Record<string, any> = {}): Promise<any> =>
  apiFetch(`/email-hub/v3/audit-log${qs(f)}`);

// Team users
export const fetchTeamUsers = (): Promise<TeamUser[]> =>
  apiFetch("/email-hub/v3/team-users");
